// sourcemap.jsx — SourceMapPro: the full source-map engine.
// Two layouts (radial depth-rings · chronological river) with tweened
// transitions, pan/zoom (drag · wheel · pinch), type/era filters, chain
// tracing with sequenced draw-in, influence sizing, and tap-to-select.
const { useState: useStateSM, useEffect: useEffectSM, useRef: useRefSM, useMemo: useMemoSM } = React;

const MAP_W = 1300, MAP_H = 800;

// ── Layouts ──────────────────────────────────────────────────────────────────
function layoutRadial(nodes, eras) {
  const cx = MAP_W / 2, cy = MAP_H / 2 - 22, SQ = 0.72;
  const pos = { center: { x: cx, y: cy } };
  const eraIdx = Object.fromEntries((eras || []).map((e, i) => [e.id, i]));
  const direct = nodes.filter((n) => n.id !== "center")
    .sort((a, b) => (eraIdx[a.era] - eraIdx[b.era]) || ((a.n || 0) - (b.n || 0)));
  let cursor = 0, ring = 0;
  while (cursor < direct.length) {
    const capacity = direct.length <= 14 ? direct.length : Math.min(direct.length - cursor, 10 + ring * 7);
    const radius = direct.length <= 14 ? 240 : 105 + ring * 70;
    direct.slice(cursor, cursor + capacity).forEach((node, index) => {
      const offset = ring % 2 ? 180 / Math.max(1, capacity) : 0;
      const angle = (-90 + offset + index * 360 / Math.max(1, capacity)) * Math.PI / 180;
      pos[node.id] = { x: cx + radius * Math.cos(angle), y: cy + radius * SQ * Math.sin(angle) };
    });
    cursor += capacity;
    ring += 1;
  }
  return pos;
}

function riverCols(eras) {
  const left = 100, right = 220;
  const colW = (MAP_W - left - right) / Math.max(1, eras.length - 1);
  return (eras || []).map((era, i) => ({ era, x: left + i * colW }));
}

function layoutRiver(nodes, eras) {
  const pos = { center: { x: MAP_W - 130, y: MAP_H / 2 } };
  riverCols(eras).forEach(({ era, x }, ci) => {
    const col = nodes.filter((n) => n.era === era.id && n.id !== "center")
      .sort((a, b) => (b.influence || 0) - (a.influence || 0));
    const rows = Math.min(11, Math.max(1, col.length));
    const lanes = Math.ceil(col.length / rows);
    const gap = Math.min(62, (MAP_H - 230) / Math.max(1, rows - 1));
    col.forEach((n, i) => {
      const lane = Math.floor(i / rows), row = i % rows;
      pos[n.id] = {
        x: x + (lane - (lanes - 1) / 2) * 25,
        y: MAP_H / 2 + (row - (rows - 1) / 2) * gap + (ci % 2 ? 10 : -10),
      };
    });
  });
  return pos;
}

// ── Tweened positions: nodes glide between layouts ───────────────────────────
function useTweenPositions(target, key) {
  const [pos, setPos] = useStateSM(target);
  const ref = useRefSM({ cur: target, raf: 0, key });
  useEffectSM(() => {
    if (ref.current.key === key) { ref.current.cur = target; setPos(target); return; }
    ref.current.key = key;
    cancelAnimationFrame(ref.current.raf);
    if (!window.canAnimate || !window.canAnimate()) { ref.current.cur = target; setPos(target); return; }
    const from = ref.current.cur, t0 = performance.now(), DUR = 720;
    let done = false;
    // wall-clock safety: if rAF is throttled/suspended, snap to the target layout
    const safety = setTimeout(() => {
      if (done) return;
      cancelAnimationFrame(ref.current.raf);
      ref.current.cur = target; setPos(target);
    }, DUR + 280);
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / DUR), e = 1 - Math.pow(1 - p, 3);
      const cur = {};
      for (const id in target) {
        const f = from[id] || target[id];
        cur[id] = { x: f.x + (target[id].x - f.x) * e, y: f.y + (target[id].y - f.y) * e };
      }
      ref.current.cur = cur; setPos(cur);
      if (p < 1) ref.current.raf = requestAnimationFrame(tick);
      else { done = true; clearTimeout(safety); }
    };
    ref.current.raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(ref.current.raf); clearTimeout(safety); };
  }, [key]);
  return pos;
}

function edgePathD(a, b, style, mode) {
  if (mode === "river") {
    const dx = (b.x - a.x) * 0.5;
    return `M ${a.x} ${a.y} C ${a.x + dx} ${a.y}, ${b.x - dx} ${b.y}, ${b.x} ${b.y}`;
  }
  if (style === "line") return `M ${a.x} ${a.y} L ${b.x} ${b.y}`;
  const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
  const dx = b.x - a.x, dy = b.y - a.y;
  return `M ${a.x} ${a.y} Q ${mx - dy * 0.14} ${my + dx * 0.14}, ${b.x} ${b.y}`;
}

// ── The map ──────────────────────────────────────────────────────────────────
function SourceMapPro({
  mode = "radial", edgeStyle = "arc", labels = "always", compact = false,
  hiddenTypes = [], era = null, selected, onSelect, activeChain, className, onEraClick, map,
}) {
  const mobileMap = window.useIsMobile();
  const MAP = map || window.MAP;
  const nodes = MAP.nodes;
  const byId = useMemoSM(() => Object.fromEntries(nodes.map((n) => [n.id, n])), [nodes]);
  const edges = useMemoSM(() => MAP.edges.filter((e) => byId[e.from] && byId[e.to]), [byId]);
  const target = useMemoSM(() => (mode === "river" ? layoutRiver(nodes, MAP.eras) : layoutRadial(nodes, MAP.eras)), [mode, nodes, MAP.eras]);
  const pos = useTweenPositions(target, `${mode}:${nodes.map((node) => node.id).join("|")}`);
  const frame = useMemoSM(() => {
    if (mode !== "radial") return { x: 0, y: 0, w: MAP_W, h: MAP_H };
    const points = Object.values(target);
    const padding = compact ? 40 : 80;
    const left = Math.min(...points.map(p => p.x)) - padding;
    const top = Math.min(...points.map(p => p.y)) - padding;
    return { x: left, y: top, w: Math.max(compact ? 300 : 500, Math.max(...points.map(p => p.x)) - left + padding), h: Math.max(compact ? 260 : 500, Math.max(...points.map(p => p.y)) - top + padding) };
  }, [target, mode, mobileMap, compact]);
  const [hover, setHover] = useStateSM(null);
  const chain = activeChain ? MAP.chains.find((c) => c.id === activeChain) : null;

  // chain paths draw in via WAAPI (visible-at-rest; safety snap if animation stalls)
  const chainPathRefs = useRefSM([]);
  useEffectSM(() => {
    if (!activeChain) return;
    const els = chainPathRefs.current.filter(Boolean);
    if (!els.length || !window.canAnimate || !window.canAnimate()) return;
    const cleanups = els.map((el, i) => {
      el.style.strokeDasharray = "1600";
      el.style.strokeDashoffset = "1600";
      const a = el.animate([{ strokeDashoffset: 1600 }, { strokeDashoffset: 0 }],
        { duration: 800, delay: 150 + i * 380, easing: window.EASE_OUT, fill: "none" });
      const clear = () => { el.style.strokeDashoffset = "0"; clearTimeout(safety); };
      a.onfinish = clear; a.oncancel = clear;
      const safety = setTimeout(() => { try { a.cancel(); } catch (e) {} clear(); }, 150 + i * 380 + 1500);
      return () => { try { a.cancel(); } catch (e) {} el.style.strokeDashoffset = "0"; clearTimeout(safety); };
    });
    return () => cleanups.forEach((fn) => fn());
  }, [activeChain]);

  // pan / zoom -----------------------------------------------------------------
  const svgRef = useRefSM(null);
  const [view, setView] = useStateSM({ x: 0, y: 0, k: 1 });
  const ptrs = useRefSM(new Map());
  const moved = useRefSM(0);

  const toLocal = (cx, cy) => {
    const r = svgRef.current.getBoundingClientRect();
    const s = Math.min(r.width / frame.w, r.height / frame.h);
    const ox = (r.width - frame.w * s) / 2, oy = (r.height - frame.h * s) / 2;
    return { x: frame.x + (cx - r.left - ox) / s, y: frame.y + (cy - r.top - oy) / s };
  };
  const zoomAt = (px, py, f) => setView((v) => {
    const k = Math.min(4, Math.max(0.45, v.k * f)); const ff = k / v.k;
    return { k, x: px - (px - v.x) * ff, y: py - (py - v.y) * ff };
  });
  const zoomCenter = (f) => {
    const r = svgRef.current.getBoundingClientRect();
    const p = toLocal(r.left + r.width / 2, r.top + r.height / 2);
    zoomAt(p.x, p.y, f);
  };

  useEffectSM(() => {
    const el = svgRef.current; if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      const p = toLocal(e.clientX, e.clientY);
      zoomAt(p.x, p.y, Math.exp(-e.deltaY * 0.0014));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    const onWinUp = (e) => ptrs.current.delete(e.pointerId);
    window.addEventListener("pointerup", onWinUp);
    return () => { el.removeEventListener("wheel", onWheel); window.removeEventListener("pointerup", onWinUp); };
  }, [frame]);

  const onDown = (e) => { ptrs.current.set(e.pointerId, { x: e.clientX, y: e.clientY }); moved.current = 0; };
  const onMove = (e) => {
    if (!ptrs.current.has(e.pointerId)) return;
    const prev = ptrs.current.get(e.pointerId);
    const cur = { x: e.clientX, y: e.clientY };
    ptrs.current.set(e.pointerId, cur);
    const all = [...ptrs.current.entries()];
    if (all.length === 1) {
      const p0 = toLocal(prev.x, prev.y), p1 = toLocal(cur.x, cur.y);
      moved.current += Math.abs(cur.x - prev.x) + Math.abs(cur.y - prev.y);
      setView((v) => ({ ...v, x: v.x + (p1.x - p0.x), y: v.y + (p1.y - p0.y) }));
    } else if (all.length === 2) {
      const other = all.find(([id]) => id !== e.pointerId);
      if (!other) return;
      const o = other[1];
      const dPrev = Math.hypot(prev.x - o.x, prev.y - o.y);
      const dCur = Math.hypot(cur.x - o.x, cur.y - o.y);
      const mid = toLocal((cur.x + o.x) / 2, (cur.y + o.y) / 2);
      if (dPrev > 0) zoomAt(mid.x, mid.y, dCur / dPrev);
      moved.current += 10;
    }
  };
  const onUp = (e) => ptrs.current.delete(e.pointerId);

  // helpers ---------------------------------------------------------------------
  const visN = (n) => n.id === "center" || (!hiddenTypes.includes(n.type) && (!era || n.era === era));
  const nodeR = (n) => (n.id === "center" ? 28 : 10 + (n.influence || 0.5) * 5);
  const chainIdx = (id) => (chain ? chain.nodes.indexOf(id) : -1);
  const chainEdge = (e) => {
    if (!chain) return -1;
    const i = chain.nodes.indexOf(e.from);
    return i >= 0 && chain.nodes[i + 1] === e.to ? i : -1;
  };

  return (
    <div className={"mappro" + (className ? " " + className : "")}>
      <svg ref={svgRef} viewBox={`${frame.x} ${frame.y} ${frame.w} ${frame.h}`} preserveAspectRatio="xMidYMid meet"
        onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
        onDoubleClick={(e) => { const p = toLocal(e.clientX, e.clientY); zoomAt(p.x, p.y, 1.6); }}
        onClick={(e) => { if (moved.current < 6 && !e.target.closest(".map-node")) onSelect && onSelect(null); }}>
        <g transform={`translate(${view.x} ${view.y}) scale(${view.k})`}>

          {/* river era columns — labels double as era filters */}
          {mode === "river" && riverCols(MAP.eras).map(({ era: er, x }) => (
            <g key={er.id} opacity={era && era !== er.id ? 0.3 : 0.9} style={{ transition: "opacity .35s", cursor: onEraClick ? "pointer" : "default" }}
              onClick={(e) => { e.stopPropagation(); if (moved.current < 6 && onEraClick) onEraClick(er.id); }}>
              <line x1={x} y1={66} x2={x} y2={MAP_H - 72} stroke="var(--border)" strokeWidth="1" strokeDasharray="2 8" />
              <text x={x} y={MAP_H - 44} textAnchor="middle" className="map-colhe">{er.he}</text>
              <text x={x} y={MAP_H - 24} textAnchor="middle" className={"map-collabel" + (era === er.id ? " on" : "")}>{er.label}</text>
            </g>
          ))}
          {mode === "river" && (
            <text x={MAP_W - 130} y={MAP_H - 24} textAnchor="middle" className="map-collabel" opacity=".9">The sicha</text>
          )}

          {/* edges */}
          {edges.map((e, i) => {
            const a = pos[e.from], b = pos[e.to];
            if (!a || !b) return null;
            const ci = chainEdge(e);
            const vis = visN(byId[e.from]) && visN(byId[e.to]);
            const mutedE = chain ? ci < 0 : !vis;
            const hot = (hover && (e.from === hover || e.to === hover)) ||
                        (selected && (e.from === selected || e.to === selected));
            const d = edgePathD(a, b, edgeStyle, mode);
            const col = window.sourceEraColor((byId[e.from] || {}).era);
            return (
              <g key={i}>
                <path d={d} fill="none" stroke={hot ? col : mutedE ? "var(--border)" : "var(--border-2)"}
                  strokeWidth={hot ? 2.4 : mutedE ? 1 : 1.5}
                  opacity={chain ? (ci >= 0 ? 0 : 0.25) : vis ? (hot ? 0.95 : 0.7) : 0.18}
                  style={{ transition: "opacity .35s, stroke .25s, stroke-width .25s" }} />
                {ci >= 0 && (
                  <path ref={(el) => (chainPathRefs.current[ci] = el)} d={d} fill="none" stroke={chain.color}
                    strokeWidth="3" strokeLinecap="round" />
                )}
              </g>
            );
          })}

          {/* nodes */}
          {nodes.map((n) => {
            const p = pos[n.id];
            if (!p) return null;
            const r = nodeR(n);
            const on = selected === n.id, hv = hover === n.id;
            const ci = chainIdx(n.id);
            // muted = structurally dimmed (small gray dot, no label) — robust everywhere
            const muted = chain ? (ci < 0 && n.id !== "center") : !visN(n);
            const col = window.sourceEraColor(n.era);
            const rr = muted ? Math.max(4.5, r * 0.5) : r;
            const showLabel = !muted && (labels === "always" || on || hv || n.id === "center");
            return (
              <g key={n.id} className="map-node" role="button" tabIndex={muted ? -1 : 0} aria-label={`${n.title}: ${n.citation || ""}`} aria-pressed={on} onKeyDown={(event) => { if(event.key === "Enter" || event.key === " "){event.preventDefault();onSelect && onSelect(on ? null : n.id);}}} transform={`translate(${p.x} ${p.y})`} opacity={muted ? 0.55 : 1}
                style={{ transition: "opacity .35s", cursor: "pointer", color: muted ? "var(--border-2)" : col }}
                onClick={(e) => { e.stopPropagation(); if (moved.current < 6) onSelect && onSelect(on ? null : n.id); }}
                onMouseEnter={() => setHover(n.id)} onMouseLeave={() => setHover(null)}>
                {n.id === "center" ? (
                  <>

                    <rect x="-80" y="-26" width="160" height="52" rx="4" fill="var(--paper)" stroke="var(--accent)" strokeWidth="1.5" />
                    <text className="map-label" y="-5" textAnchor="middle">Current text</text>
                    <text className="map-cite" y="13" textAnchor="middle">{String(n.title || "").slice(0,28)}</text>
                  </>
                ) : (
                  <>
                    {(on || hv) && !muted && <circle r={rr + 6} fill="none" stroke={col} strokeWidth="1.5" opacity=".55" />}
                    <circle r={rr} fill="var(--paper)" stroke={muted ? "var(--border-2)" : col} strokeWidth="1.5" className="map-dot" />
                    {!muted && (n.n != null
                      ? <text className="map-num" style={{fill:col}} y="4">{n.n}</text>
                      : <circle r="3" fill="var(--canvas)" opacity=".85" />)}
                  </>
                )}
                {ci >= 0 && n.id !== "center" && (
                  <g transform={`translate(${rr * 0.78 + 7} ${-rr * 0.78 - 7})`}>
                    <circle r="9" fill={chain.color} stroke="var(--canvas)" strokeWidth="1.5" />
                    <text className="map-stepnum" y="3.5">{ci + 1}</text>
                  </g>
                )}
                {showLabel && n.id !== "center" && (
                  <g style={{ pointerEvents: "none" }}>
                    {n.id === "center" ? (
                      <>
                        <text className="map-label" y={rr + 26} textAnchor="middle">Current text</text>
                        <text className="map-cite" y={rr + 43} textAnchor="middle">{n.title}</text>
                      </>
                    ) : (
                      <>
                        <text className="map-label" y={rr + 17} textAnchor="middle">{n.title}</text>
                        <text className="map-cite" y={rr + 31} textAnchor="middle">{n.citation}</text>
                      </>
                    )}
                  </g>
                )}
              </g>
            );
          })}
        </g>
      </svg>

      {!compact && <div className="map-zoom">
        <button onClick={() => zoomCenter(1.3)} aria-label="Zoom in"><Icon d="plus" size={15} /></button>
        <button onClick={() => zoomCenter(0.75)} aria-label="Zoom out"><span style={{ fontSize: 15, fontWeight: 700 }}>−</span></button>
        <button onClick={() => setView({ x: 0, y: 0, k: 1 })} aria-label="Fit to view"><Icon d="compass" size={15} /></button>
      </div>}
    </div>
  );
}

Object.assign(window, { SourceMapPro });

function SourceEraLegend({ map, selected, onSelect }) {
  const present = new Set(map.nodes.filter(n => n.id !== "center").map(n => n.era));
  return <div className="source-era-legend" aria-label="Source eras">{map.eras.filter(e => present.has(e.id)).map(e => onSelect ? <button key={e.id} aria-pressed={selected === e.id} onClick={() => onSelect(selected === e.id ? null : e.id)}><i style={{background:window.sourceEraColor(e.id)}}/>{e.label}</button> : <span key={e.id}><i style={{background:window.sourceEraColor(e.id)}}/>{e.label}</span>)}</div>;
}
window.SourceEraLegend = SourceEraLegend;
